/* Mikro-site kökü: içerik yükleme, dil, sekmeler ve deep link. */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { SiteContent } from "../shared/types.js";
import { api } from "./api.js";
import { useLang, ui } from "./lib/i18n.js";
import { BottomNav } from "./components/BottomNav.js";
import { isTabId, type TabId } from "./tabs.js";
import { Program } from "./tabs/Program.js";
import { DressCodeTab } from "./tabs/DressCodeTab.js";
import { WorkshopsTab } from "./tabs/WorkshopsTab.js";
import { EveningTab } from "./tabs/EveningTab.js";
import { SpeakersTab } from "./tabs/SpeakersTab.js";
import { AdminApp } from "./admin/AdminApp.js";

/** Açılışta her zaman Sekme 1; ancak paylaşılan bir deep link varsa o sekme. */
function tabFromHash(): TabId {
  const raw = window.location.hash.replace(/^#/, "");
  return isTabId(raw) ? raw : "program";
}

export function App() {
  if (window.location.pathname.startsWith("/yonetim")) return <AdminApp />;
  return <Site />;
}

function Site() {
  const [lang, setLang] = useLang();
  const [content, setContent] = useState<SiteContent | null>(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState<TabId>(tabFromHash);
  const scrollMemory = useRef<Partial<Record<TabId, number>>>({});
  const previousTab = useRef<TabId>(tab);

  const load = useCallback(() => {
    setFailed(false);
    api
      .content()
      .then(setContent)
      .catch(() => setFailed(true));
  }, []);

  useEffect(load, [load]);

  /* Geri/ileri tuşları ve dışarıdan gelen linkler */
  useEffect(() => {
    const onHash = () => setTab(tabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const select = useCallback((next: TabId) => {
    scrollMemory.current[previousTab.current] = window.scrollY;
    if (window.location.hash.replace(/^#/, "") !== next) {
      history.pushState(null, "", "#" + next);
    }
    setTab(next);
  }, []);

  /* Sekme değişince o sekmenin bıraktığı yere dön */
  useLayoutEffect(() => {
    if (previousTab.current !== tab) {
      previousTab.current = tab;
      window.scrollTo({ top: scrollMemory.current[tab] ?? 0, behavior: "auto" });
    }
  }, [tab]);

  return (
    <div className="site">
      <div className="topbar">
        <div className="topbar__inner">
          <div className="mark">
            <img
              className="mark__badge"
              src="/gorseller/monogram.png"
              alt=""
              width={200}
              height={225}
            />
            <span className="mark__name">Boring People</span>
          </div>
          <div className="lang" role="group" aria-label="Language">
            {(["tr", "en"] as const).map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={lang === code}
                onClick={() => setLang(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="wrap" id={"panel-" + tab} role="tabpanel">
        {!content && !failed && (
          <p className="center dim" style={{ padding: "80px 0" }}>
            {ui("loading", lang)}
          </p>
        )}

        {failed && (
          <div className="center" style={{ padding: "80px 0" }}>
            <p className="muted">{ui("offline", lang)}</p>
            <button type="button" className="btn btn--ghost" style={{ marginTop: 16 }} onClick={load}>
              {ui("retry", lang)}
            </button>
          </div>
        )}

        {content && (
          <div key={tab} className="tab-enter">
            {tab === "program" && <Program content={content} lang={lang} />}
            {tab === "dresscode" && <DressCodeTab content={content} lang={lang} />}
            {tab === "atolyeler" && <WorkshopsTab content={content} lang={lang} />}
            {tab === "aksam" && <EveningTab content={content} lang={lang} />}
            {tab === "konusmacilar" && <SpeakersTab content={content} lang={lang} />}
          </div>
        )}
      </main>

      <BottomNav active={tab} lang={lang} onSelect={select} />
    </div>
  );
}
